import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useQuery } from "@apollo/client";
import { sortBy } from "lodash";
import MultiSelect from "@/components/MultiSelect";
import { GET_UNIVERSITIES } from "../../queries";
import type { University } from "../types";
import type { FormValues } from "../validation/schema";

export interface EventsUniversitiesQueryData {
  universities: University[];
}

const UniversityDropdown = ({
  disabled,
  onUniversitiesChange
}: {
  disabled?: boolean;
  onUniversitiesChange?: (universities: University[]) => void;
}) => {
  const {
    control,
    formState: { errors }
  } = useFormContext<FormValues>();
  const { data, loading } = useQuery<EventsUniversitiesQueryData>(GET_UNIVERSITIES);

  const universities = useMemo(() => sortBy(data?.universities ?? [], (u) => u.name.toLowerCase()), [data]);

  return (
    <Controller
      name="universityIds"
      control={control}
      render={({ field: { value, onChange } }) => (
        <MultiSelect
          label="Managing University"
          required
          skipShowAll
          disabled={disabled || loading}
          options={universities.map((university) => ({ value: university.id, label: university.name }))}
          selectedValues={value ?? []}
          onSelect={(selectedUniversityId, checked) => {
            const selectedIds = value ?? [];
            const nextSelectedIds = checked
              ? [...selectedIds, selectedUniversityId]
              : selectedIds.filter((universityId) => universityId !== selectedUniversityId);
            onChange(nextSelectedIds);
            onUniversitiesChange?.(
              nextSelectedIds
                .map((id) => universities.find((u) => u.id === id))
                .filter((u): u is University => u !== undefined)
            );
          }}
          onClear={() => {
            onChange([]);
            onUniversitiesChange?.([]);
          }}
          error={!!errors.universityIds}
          errorText={errors.universityIds?.message as string | undefined}
          defaultLabel={loading ? "Loading..." : "Show All"}
        />
      )}
    />
  );
};

export default UniversityDropdown;
